import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LOADING, PLAYING, STOPPED, useAudioPlayerContext } from "../../contexts/AudioPlayerContext";
import { PLAY, STOP } from "../../redux/actions/PlayerActions";
import IconButton from "../IconButton";
import Loader from "../Loader";
import store from "../../redux/store";

const PlayButton = ({
  voice = "ethan",
  id,
  text = "",
  type = "rounded",
  showText = false,
  iconSize = "size-7",
  preventPropagation = true,
}) => {
  const player = useSelector((state) => state.player);
  const organization = useSelector((state) => state.info.organization);
  const dispatch = useDispatch();

  const { playAudio, eventEmitter, stopAudio } = useAudioPlayerContext();
  const [playingStatus, setPlayingStatus] = useState(STOPPED);
  const audioRef = useRef(null);

  const playURL = useMemo(() => {
    const urlParams = new URLSearchParams({
      text: text,
      organizationId: organization.id,
    });

    const url = `${window.appConfig.API_GATEWAY_URL}/snippet/voices/${voice}/play?${urlParams.toString()}`;

    return url;
  }, [text, voice]);

  const icon = useMemo(() => {
    if (text === "") {
      return "/assets/icons/actions/v2/play-disabled.svg";
    }
    if (playingStatus !== STOPPED) {
      return "/assets/icons/actions/v2/stop.svg";
    }
    return "/assets/icons/actions/v2/play.svg";
  }, [playingStatus, text]);

  const hoverIcon = useMemo(() => {
    if (playingStatus !== STOPPED) {
      return "/assets/icons/actions/v2/stop-hover.svg";
    }
    return "/assets/icons/actions/v2/play-hover.svg";
  }, [playingStatus]);

  const onClick = async (event) => {
    if (!text) return;

    if (preventPropagation) {
      event.stopPropagation();
    }
    switch (playingStatus) {
      case STOPPED:
        setPlayingStatus(LOADING);
        playAudio(audioRef.current, id);
        dispatch({ type: PLAY });

        try {
          // Fetch audio with authorization header
          const token = store.getState().info.token;
          const response = await fetch(playURL, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch audio: ${response.status}`);
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          audioRef.current.src = blobUrl;
          await audioRef.current.play();
          setPlayingStatus(PLAYING);

          // Clean up blob URL when audio ends
          const cleanupBlob = () => {
            URL.revokeObjectURL(blobUrl);
          };
          audioRef.current.addEventListener("ended", cleanupBlob, { once: true });
          audioRef.current.addEventListener("pause", cleanupBlob, { once: true });
        } catch (error) {
          console.error("Error playing audio:", error);
          setPlayingStatus(STOPPED);
          dispatch({ type: STOP });
        }
        break;
      default:
        audioRef.current.pause();
        setPlayingStatus(STOPPED);
        dispatch({ type: STOP });
        break;
    }
  };

  useEffect(() => {
    const stopListener = (stoppedComponentId) => {
      if (id !== stoppedComponentId) {
        setPlayingStatus(STOPPED);
      }
    };

    eventEmitter.setMaxListeners(50);
    eventEmitter.on("stop", stopListener);

    return () => {
      eventEmitter.removeListener("stop", stopListener);
    };
  }, [eventEmitter, id]);

  return (
    <div className="relative" onClick={onClick}>
      <audio ref={audioRef} className="" onEnded={() => setPlayingStatus(STOPPED)} />
      <IconButton
        iconSize={iconSize}
        icon={icon}
        hoverIcon={hoverIcon}
        disabled={text === ""}
        text={showText ? "Preview" : ""}
      />
      {playingStatus === LOADING && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Loader size={24} border="border-2" color={"black"} />
        </div>
      )}
    </div>
  );
};

export default PlayButton;
